import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { usePaginatedApi, useAsyncOperation } from '../hooks/useApi';
import { loadsAPI, clientsAPI, systemConfigAPI, usersAPI } from '../services/api';
import { formatWeight, formatDateTime, getTransactionTypeDisplay, loadHasWeightsForCharged } from '../utils/helpers';
import { getLoadClientDisplayName, getLoadClientContactName } from '../utils/loadClientDisplay';
import Table from '../components/Table';
import Button from '../components/Button';
import Input, { Select } from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';
import LoadDetailViewModal, { getLoadStatusBadgeColor } from '../components/LoadDetailViewModal';
import AddTicketModal from '../components/AddTicketModal';
import EditLoadModal from '../components/EditLoadModal';
import LoadSupplementaryTaxModal from '../components/LoadSupplementaryTaxModal';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY } from '../constants/systemConfigKeys';
import {
  ArchiveBoxIcon,
  PrinterIcon,
  EnvelopeIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const LOAD_FILTER_STATUS_ORDER = ['REQUESTED', 'REJECTED', 'ACCEPTED', 'LOADED', 'DELIVERED'];

/** Default /loads status checkboxes: Demande + Rejeté + Accepté + Chargé */
const LOADS_PAGE_DEFAULT_STATUSES = ['REQUESTED', 'REJECTED', 'ACCEPTED', 'LOADED'];

/** Normal list pagination page size (server default matches). */
const LOADS_LIST_PAGE_SIZE = 20;
/**
 * When the search box is non-empty, fetch enough rows in one request so results are not limited to the first UI page.
 * (Search still runs on the server across all loads; this only raises `limit`.)
 */
const LOADS_SEARCH_FETCH_LIMIT = 5000;

function sortStatusFilterSelection(selected) {
  const set = new Set(Array.isArray(selected) ? selected : []);
  return LOAD_FILTER_STATUS_ORDER.filter((s) => set.has(s));
}

function getLoadsPageDefaultStatusFilter({ isPeseur = false } = {}) {
  return sortStatusFilterSelection(
    isPeseur ? LOADS_PAGE_DEFAULT_STATUSES : LOADS_PAGE_DEFAULT_STATUSES
  );
}

function statusFilterToQueryParam(statusArr) {
  const sorted = sortStatusFilterSelection(statusArr);
  return sorted.length ? sorted.join(',') : '';
}

/** No tare weight stored on the load row (kg); truck default does not count. */
function loadMissingRecordedTareWeightForRow(load) {
  const ew = load?.emptyWeight;
  if (ew == null || ew === '') return true;
  const n = Number(ew);
  return !Number.isFinite(n) || n <= 0;
}

function getInitialLoadsFilters({ isPeseur = false, peseurId = '' } = {}) {
  return {
    search: '',
    clientId: '',
    transactionType: '',
    status: getLoadsPageDefaultStatusFilter({ isPeseur }),
    startDate: '',
    endDate: '',
    secondPassPending: false,
    peseurId: peseurId || ''
  };
}

const LoadsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openLoadFromNavHandledRef = useRef(null);
  const { isManagerOrAdmin, isManager, isAdmin, isOperator, isDriver, isPeseur, isSuper, user } = useAuth();
  const superReadOnly = isSuper();
  const peseurIdFromUrl = isManager() ? String(searchParams.get('peseurId') || '').trim() : '';
  const [filters, setFilters] = useState(() =>
    getInitialLoadsFilters({ isPeseur: isPeseur(), peseurId: peseurIdFromUrl })
  );
  const [peseurFilterUser, setPeseurFilterUser] = useState(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const statusFilterKey = sortStatusFilterSelection(filters.status).join(',');
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadDetailReturn, setLoadDetailReturn] = useState(null);
  const [rowEmailModalOpen, setRowEmailModalOpen] = useState(false);
  const [rowEmailLoad, setRowEmailLoad] = useState(null);
  const [rowEmailAddress, setRowEmailAddress] = useState('');
  
  // CRUD modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTicketOpenFromAcceptedAwaitingTare, setAddTicketOpenFromAcceptedAwaitingTare] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTsModal, setShowTsModal] = useState(false);
  const [pendingStatusLoad, setPendingStatusLoad] = useState(null);
  const [addTicketContinueLoadId, setAddTicketContinueLoadId] = useState(null);
  const [preTareRequestsAllowed, setPreTareRequestsAllowed] = useState(false);
  const [clientEligibleBilling, setClientEligibleBilling] = useState(false);

  // Track last edited load for scroll-to functionality
  const [lastEditedLoadId, setLastEditedLoadId] = useState(null);

  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    if (!isOperator() || !user?.clientId) {
      setPreTareRequestsAllowed(false);
      setClientEligibleBilling(false);
      return () => {
        cancelled = true;
      };
    }

    const loadEligibility = async () => {
      try {
        const [cfgRes, clientRes] = await Promise.allSettled([
          systemConfigAPI.getByKey(CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY),
          clientsAPI.getById(user.clientId)
        ]);
        if (cancelled) return;

        const featureAllowed =
          cfgRes.status === 'fulfilled'
            ? Boolean(cfgRes.value?.data?.value)
            : cfgRes.reason?.response?.status === 404
              ? false
              : (() => {
                  throw cfgRes.reason;
                })();
        const billingType =
          clientRes.status === 'fulfilled'
            ? String(clientRes.value?.data?.client?.billingType || clientRes.value?.data?.billingType || '')
            : '';
        setPreTareRequestsAllowed(featureAllowed);
        setClientEligibleBilling(['CREDIT', 'PAYING'].includes(billingType));
      } catch {
        if (!cancelled) {
          setPreTareRequestsAllowed(false);
          setClientEligibleBilling(false);
        }
      }
    };

    loadEligibility();
    return () => {
      cancelled = true;
    };
  }, [isOperator, user?.clientId]);

  useEffect(() => {
    const pid = isManager() ? String(searchParams.get('peseurId') || '').trim() : '';
    if (!isManager() && searchParams.get('peseurId')) {
      setSearchParams({}, { replace: true });
      return;
    }
    setFilters((prev) => (prev.peseurId === pid ? prev : { ...prev, peseurId: pid }));
    if (pid) setMoreFiltersOpen(true);
  }, [searchParams, isManager, setSearchParams]);

  useEffect(() => {
    const pid = String(filters.peseurId || '').trim();
    if (!pid || !isManager()) {
      setPeseurFilterUser(null);
      return undefined;
    }
    let cancelled = false;
    usersAPI
      .getById(pid)
      .then((res) => {
        if (!cancelled) setPeseurFilterUser(res?.data?.user || null);
      })
      .catch(() => {
        if (!cancelled) setPeseurFilterUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.peseurId, isManager]);

  const clearPeseurFilter = () => {
    setSearchParams({}, { replace: true });
    setFilters((prev) => ({ ...prev, peseurId: '' }));
  };

  const peseurFilterDisplayName = peseurFilterUser
    ? `${peseurFilterUser.firstName || ''} ${peseurFilterUser.lastName || ''}`.trim() ||
      peseurFilterUser.username ||
      filters.peseurId
    : filters.peseurId;

  // Open load modal when navigating from another page (e.g. bill or certificate details)
  useEffect(() => {
    const openLoadId = location.state?.openLoadId;
    if (!openLoadId) {
      openLoadFromNavHandledRef.current = null;
      setLoadDetailReturn(null);
      return;
    }
    if (openLoadFromNavHandledRef.current === openLoadId) return;
    openLoadFromNavHandledRef.current = openLoadId;

    const returnNav = {};
    if (location.state?.openCertificateId) {
      returnNav.openCertificateId = String(location.state.openCertificateId);
    }
    if (location.state?.openBillId) {
      returnNav.openBillId = String(location.state.openBillId);
    }
    setLoadDetailReturn(
      returnNav.openCertificateId || returnNav.openBillId ? returnNav : null
    );

    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        const response = await loadsAPI.getById(openLoadId);
        setSelectedLoad(response.data.load);
        setShowLoadModal(true);
      } catch (err) {
        console.error('Error opening load from navigation:', err);
        toast.error(t('loads.loadDetailsFetchError'));
      }
    })();
  }, [location.state?.openLoadId, location.state?.openCertificateId, location.state?.openBillId, location.pathname, navigate, t, toast]);

  // Fetch loads with pagination
  const {
    data: loads,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh,
  } = usePaginatedApi(loadsAPI.getAll, filters);

  // Fetch clients for filter dropdown
  const { data: clients } = usePaginatedApi(clientsAPI.getAll, { limit: 100 });

  // Async operations
  const { execute: executeAsync, loading: actionLoading } = useAsyncOperation();

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleStatusFilter = (code) => {
    setFilters((prev) => {
      const cur = sortStatusFilterSelection(prev.status);
      const has = cur.includes(code);
      const next = has ? cur.filter((s) => s !== code) : [...cur, code];
      return { ...prev, status: sortStatusFilterSelection(next) };
    });
  };

  // Debounced filters for automatic type-ahead
  const filterTimeoutRef = useRef(null);
  const updateParamsRef = useRef(updateParams);
  
  // Keep updateParams ref up to date
  useEffect(() => {
    updateParamsRef.current = updateParams;
  }, [updateParams]);

  // Scroll to and highlight edited load after data loads
  useEffect(() => {
    if (lastEditedLoadId && loads && loads.length > 0 && !loading) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Try to find the row in desktop table view
        const desktopRow = document.querySelector(`tr[data-load-id="${lastEditedLoadId}"]`);
        // Try to find the card in mobile view
        const mobileCard = document.querySelector(`div[data-load-id="${lastEditedLoadId}"]`);
        
        const element = desktopRow || mobileCard;
        
        if (element) {
          // Scroll to element with smooth behavior
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add highlight class
          element.classList.add('highlight-edited');
          
          // Remove highlight after animation (2 seconds)
          setTimeout(() => {
            element.classList.remove('highlight-edited');
            setLastEditedLoadId(null); // Clear the ID
          }, 2000);
        } else {
          // If element not found, clear the ID after a short delay
          // This handles cases where the load might be on a different page
          setTimeout(() => {
            setLastEditedLoadId(null);
          }, 500);
        }
      }, 100);
    }
  }, [lastEditedLoadId, loads, loading]);
  
  useEffect(() => {
    // Clear existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Set new timeout for debounced filter updates
    filterTimeoutRef.current = setTimeout(() => {
      const params = {};
      // Always include search parameter to ensure it's updated (even if empty to clear)
      const searchValue = filters.search.trim();
      params.search = searchValue || '';
      
      // Include other filters only if they have values
      if (filters.clientId) params.clientId = filters.clientId;
      if (filters.transactionType) params.transactionType = filters.transactionType;
      params.status = statusFilterKey;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      // Must always set so usePaginatedApi merge replaces prior 'true' when unchecked (omit alone leaves stale param).
      params.secondPassPending = filters.secondPassPending ? 'true' : '';
      params.peseurId = String(filters.peseurId || '').trim();
      // Same: merged params can retain excludeDelivered from elsewhere and hide DELIVERED; this page uses status checkboxes only.
      params.excludeDelivered = '';

      params.limit = searchValue ? LOADS_SEARCH_FETCH_LIMIT : LOADS_LIST_PAGE_SIZE;

      updateParamsRef.current(params);
    }, 500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [
    filters.search,
    filters.clientId,
    filters.transactionType,
    statusFilterKey,
    filters.startDate,
    filters.endDate,
    filters.secondPassPending,
    filters.peseurId
  ]);

  const resetFilters = () => {
    // Clear any pending debounced filter update
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    const defaultStatuses = getLoadsPageDefaultStatusFilter({ isPeseur: isPeseur() });
    const emptyFilters = {
      search: '',
      clientId: '',
      transactionType: '',
      status: defaultStatuses,
      startDate: '',
      endDate: '',
      secondPassPending: false,
      peseurId: ''
    };
    setFilters(emptyFilters);
    if (isManager()) setSearchParams({}, { replace: true });
    
    // Explicitly clear all filter parameters
    updateParams({
      search: '',
      clientId: '',
      transactionType: '',
      status: statusFilterToQueryParam(defaultStatuses),
      startDate: '',
      endDate: '',
      secondPassPending: false,
      peseurId: '',
      excludeDelivered: '',
      limit: LOADS_LIST_PAGE_SIZE,
      page: 1
    });
  };

  const handleViewLoad = async (load) => {
    try {
      // Fetch full load details including hasCertificate and certificateNumber
      const response = await loadsAPI.getById(load.id);
      setSelectedLoad(response.data.load);
      setShowLoadModal(true);
    } catch (error) {
      console.error('Error fetching load details:', error);
      // Fallback to using the load from table if fetch fails
      setSelectedLoad(load);
      setShowLoadModal(true);
    }
  };

  const handlePrintTicket = async (load) => {
    await executeAsync(
      async () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
        const response = await loadsAPI.getTicketPDF(load.id, currentLanguage);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url);
        printWindow.onload = () => {
          printWindow.print();
        };
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketGeneratedSuccess'),
      }
    );
  };

  const handleEmailTicketFromRow = (load) => {
    setRowEmailLoad(load);
    setRowEmailAddress(load.client?.contactEmail || load.transporteur?.contactEmail || '');
    setRowEmailModalOpen(true);
  };

  const sendRowTicketEmail = async () => {
    if (!rowEmailLoad?.id) return;
    if (!rowEmailAddress.trim()) {
      toast.error(t('loads.enterEmailError'));
      return;
    }
    await executeAsync(
      async () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
        await loadsAPI.sendTicket(rowEmailLoad.id, { email: rowEmailAddress, lang: currentLanguage });
        setRowEmailModalOpen(false);
        setRowEmailAddress('');
        setRowEmailLoad(null);
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketEmailSent'),
      }
    );
  };

  // CRUD handlers
  const handleAddLoad = () => {
    if (!(isPeseur() || (isOperator() && preTareRequestsAllowed && clientEligibleBilling))) return;
    setAddTicketContinueLoadId(null);
    setAddTicketOpenFromAcceptedAwaitingTare(false);
    setShowAddModal(true);
  };

  const handleEditLoad = async (load) => {
    if (load?.hasCertificate) {
      toast.warning(t('loads.crud.certificateGeneratedWarning'));
      return;
    }
    try {
      // Fetch full load details including hasCertificate flag
      const response = await loadsAPI.getById(load.id);
      if (response?.data?.load?.hasCertificate) {
        toast.warning(t('loads.crud.certificateGeneratedWarning'));
        return;
      }
      const fullLoad = response.data.load;
      if (
        isPeseur() &&
        String(fullLoad.status || '').toUpperCase() === 'ACCEPTED' &&
        loadMissingRecordedTareWeightForRow(fullLoad)
      ) {
        setAddTicketContinueLoadId(fullLoad.id);
        setAddTicketOpenFromAcceptedAwaitingTare(true);
        setShowAddModal(true);
        return;
      }
      setSelectedLoad(fullLoad);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching load details:', error);
      // Fallback to using the load from table if fetch fails
      setSelectedLoad(load);
      setShowEditModal(true);
    }
  };

  const handleDeleteLoad = (load) => {
    setSelectedLoad(load);
    setShowDeleteModal(true);
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    setAddTicketContinueLoadId(null);
    setAddTicketOpenFromAcceptedAwaitingTare(false);
    refresh();
  };

  const handleEditSuccess = (editedLoadId) => {
    // Store the edited load ID before closing modal
    if (editedLoadId) {
      setLastEditedLoadId(editedLoadId);
    }
    setShowEditModal(false);
    setSelectedLoad(null);
    refresh();
    toast.success(t('loads.crud.loadUpdated'));
  };

  const confirmDelete = async () => {
    await executeAsync(
      async () => {
        await loadsAPI.delete(selectedLoad.id);
        setShowDeleteModal(false);
        setSelectedLoad(null);
        refresh();
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.crud.loadDeleted'),
      }
    );
  };

  const performStatusChange = async (load, statusPayload) => {
    const targetStatus = typeof statusPayload === 'string' ? statusPayload : statusPayload?.status;

    await executeAsync(
      async () => {
        try {
          await loadsAPI.updateStatus(load.id, statusPayload);
          refresh();
        } catch (error) {
          // Enrich LOADED validation errors (e.g. missing weights) for a clearer message.
          if (error.response?.status === 400 && targetStatus === 'LOADED' && error.response.data) {
            const details = error.response.data.details;
            if (details) {
              const missing = [];
              if (details.emptyWeightMissing) missing.push('empty weight');
              if (details.loadedWeightMissing) missing.push('loaded weight');
              if (missing.length > 0) {
                error.response.data.message =
                  `Cannot mark as LOADED: Missing ${missing.join(' and ')}. Please edit the load to add the missing weight information.`;
              }
            }
          }
          throw error;
        }
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.actions.statusUpdated'),
      }
    );
  };

  const handleStatusChange = async (load, newStatus) => {
    if (!load || !newStatus || load.status === newStatus) {
      return;
    }

    if (newStatus === 'LOADED') {
      setPendingStatusLoad(load);
      setShowTsModal(true);
      return;
    }

    try {
      await performStatusChange(load, newStatus);
    } catch {
      /* executeAsync already showed toast (e.g. 403) */
    }
  };

  const handleConfirmTs = async ({ tsOptionCode, tsHours }) => {
    if (!pendingStatusLoad) return;

    const hoursNum =
      tsHours === undefined || tsHours === null || tsHours === ''
        ? 0
        : Number(tsHours);

    try {
      await performStatusChange(pendingStatusLoad, {
        status: 'LOADED',
        tsOptionCode,
        tsHours: Number.isFinite(hoursNum) ? hoursNum : 0
      });
      setShowTsModal(false);
      setPendingStatusLoad(null);
    } catch {
      /* Error toast already shown by executeAsync */
    }
  };

  const handleCloseTsModal = () => {
    setShowTsModal(false);
    setPendingStatusLoad(null);
  };

  // Helper function to get available status transitions (next and previous)
  const getAvailableStatuses = (currentStatus, load = null) => {
    if (!currentStatus) return [];
    
    const statusFlow = ['REQUESTED', 'REJECTED', 'ACCEPTED', 'LOADED', 'DELIVERED'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    const hasRecordedWeights = loadHasWeightsForCharged(load);
    
    if (currentIndex === -1) return [];
    
    const availableStatuses = [];
    
    // Add previous status (if not at the beginning)
    if (currentIndex > 0) {
      const previousStatus = statusFlow[currentIndex - 1];
      if (!(previousStatus === 'REQUESTED' && hasRecordedWeights)) {
        availableStatuses.push(previousStatus);
      }
    }
    
    // Add current status
    availableStatuses.push(currentStatus);
    
    // Add next status (if not at the end)
    if (currentIndex < statusFlow.length - 1) {
      const nextStatus = statusFlow[currentIndex + 1];
      
      if (nextStatus === 'LOADED' && load) {
        if (loadHasWeightsForCharged(load)) {
          availableStatuses.push(nextStatus);
        }
      } else if (nextStatus !== 'LOADED') {
        availableStatuses.push(nextStatus);
      }
    }
    
    return availableStatuses;
  };

  const allColumns = [
    {
      key: 'loadDate',
      title: t('loads.loadDate'),
      sortable: true,
      render: (value) => formatDateTime(value),
    },
    {
      key: 'truck',
      title: t('tables.truck'),
      render: (value, row) => {
        // Display only the driver explicitly assigned to this load.
        const assignedDriver = row.driverEntity || row.driver || null;
        const driverName = assignedDriver
          ? `${assignedDriver.firstName || ''} ${assignedDriver.lastName || ''}`.trim()
          : '';
        
        return (
          <div>
            <div className="font-medium">{row.truck?.plateNumber}</div>
            {driverName && (
              <div className="text-xs text-gray-500">{driverName}</div>
            )}
          </div>
        );
      },
    },
    ...(isOperator() ? [] : [{
      key: 'client',
      title: t('tables.client'),
      render: (value, row) => {
        const name = getLoadClientDisplayName(row);
        const contact = getLoadClientContactName(row);
        return (
          <div>
            <div className="font-medium text-gray-900">{name || '—'}</div>
            {contact ? <div className="text-xs text-gray-500">{contact}</div> : null}
          </div>
        );
      },
    }]),
    {
      key: 'product',
      title: t('tables.product'),
      render: (value, row) => row.product?.name,
    },
    // Hide transaction type and net weight columns for Admin, Manager, and Driver users
    ...(!isAdmin() && !isManagerOrAdmin() && !isDriver() ? [{
      key: 'transactionType',
      title: t('weighing.transactionType'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'IMPORT' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
        }`}>
          {getTransactionTypeDisplay(value)}
        </span>
      ),
    },
    {
      key: 'netWeight',
      title: t('weighing.netWeight'),
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="font-semibold text-blue-600">
          {formatWeight(value, 'ton', { noUnit: true })}
        </span>
      ),
    }] : []),
    {
      key: 'status',
      title: t('loads.status'),
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLoadStatusBadgeColor(row.status)}`}>
          {t(`loads.statuses.${row.status?.toLowerCase()}`)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: 'min(22rem, 42vw)',
      minWidth: 'min(22rem, 42vw)',
      render: (value, row) => {
        const isPaidPonctuelLoad =
          row.weighingBill?.billKind === 'WEIGHING_ONLY' &&
          row.weighingBill?.status === 'PAID';
        const hasManagerStatusSelector = isManagerOrAdmin() && !superReadOnly;
        const hasOperatorDeliveredAction = (isOperator() || isDriver()) && row.status === 'LOADED';
        const hasPeseurDeliveredPaidPonctuelAction = isPeseur() && isPaidPonctuelLoad && row.status === 'LOADED';
        const hasPeseurAcceptAction = isPeseur() && row.status === 'REQUESTED';
        const hasPeseurLoadedAction = isPeseur() && row.status === 'ACCEPTED' && loadHasWeightsForCharged(row);
        const showContinueWeighing =
          isPeseur() &&
          row.status === 'ACCEPTED' &&
          row.emptyWeight != null &&
          (row.loadedWeight === null || row.loadedWeight === undefined);
        const hasPeseurRequestedAction =
          isPeseur() &&
          row.status !== 'DELIVERED' &&
          row.status !== 'REQUESTED' &&
          row.status !== 'ACCEPTED' &&
          !loadHasWeightsForCharged(row);
        const hasStatusActions =
          hasManagerStatusSelector ||
          hasOperatorDeliveredAction ||
          hasPeseurAcceptAction ||
          hasPeseurLoadedAction ||
          hasPeseurRequestedAction ||
          showContinueWeighing;

        return (
        <div className="flex min-w-0 w-full flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {hasStatusActions && (
            <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1.5 border border-slate-200/90 shadow-sm">
              {hasManagerStatusSelector && (
                <div className="inline-flex items-center">
                  <Select
                    value={row.status}
                    onChange={(e) => handleStatusChange(row, e.target.value)}
                    className="text-xs min-w-[70px]"
                    size="sm"
                    options={getAvailableStatuses(row.status, row).map(status => ({
                      value: status,
                      label: t(`loads.statuses.${status.toLowerCase()}`)
                    }))}
                    placeholder=""
                  />
                </div>
              )}
              {isOperator() && row.status === 'LOADED' && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleStatusChange(row, 'DELIVERED')} 
                  title={t('loads.actions.markDelivered')}
                  className="text-green-600 hover:text-green-700 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                </Button>
              )}
              {isDriver() && row.status === 'LOADED' && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleStatusChange(row, 'DELIVERED')} 
                  title={t('loads.actions.markDelivered')}
                  className="text-green-600 hover:text-green-700 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                </Button>
              )}
              {hasPeseurAcceptAction && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleStatusChange(row, 'ACCEPTED')}
                  title={t('loads.actions.acceptLoad')}
                  className="text-blue-700 hover:text-blue-800 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  {t('loads.actions.acceptLoad')}
                </Button>
              )}
              {hasPeseurLoadedAction && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleStatusChange(row, 'LOADED')}
                  title={t('loads.statuses.loaded')}
                  className="text-purple-700 hover:text-purple-800 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  {t('loads.statuses.loaded')}
                </Button>
              )}
              {showContinueWeighing && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setAddTicketContinueLoadId(row.id);
                    setAddTicketOpenFromAcceptedAwaitingTare(false);
                    setShowAddModal(true);
                  }}
                  title={t('loads.continueWeighing')}
                  className="text-indigo-700 hover:text-indigo-800 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  {t('loads.continueWeighing')}
                </Button>
              )}
              {hasPeseurRequestedAction && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleStatusChange(row, 'REQUESTED')}
                  title={t('loads.statuses.requested')}
                  className="text-amber-800 hover:text-amber-900 bg-white/80 hover:bg-white border border-slate-200/80"
                >
                  {t('loads.statuses.requested')}
                </Button>
              )}
            </div>
          )}

          <div className="flex min-w-0 flex-wrap items-center gap-1 sm:gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleViewLoad(row)}
            title={t('loads.actions.view')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {row.ticket && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handlePrintTicket(row)}
              loading={actionLoading}
              title={t('loads.actions.print')}
            >
              <PrinterIcon className="h-4 w-4" />
            </Button>
          )}
          {row.ticket && !superReadOnly && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEmailTicketFromRow(row)}
              loading={actionLoading}
              title={t('loads.actions.email')}
            >
              <EnvelopeIcon className="h-4 w-4" />
            </Button>
          )}
          
          {(() => {
            const cannotEditRequestedAsStaff =
              row.status === 'REQUESTED' &&
              ['MANAGER', 'PESEUR'].includes(String(user?.role || '').toUpperCase());
            const canShowEditLoad =
              !superReadOnly &&
              !cannotEditRequestedAsStaff &&
              (isManagerOrAdmin() ||
                (isPeseur() && row.status !== 'DELIVERED') ||
                (isOperator() && row.status === 'REQUESTED'));
            return (
              canShowEditLoad &&
              !row.hasCertificate &&
              !isPaidPonctuelLoad && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEditLoad(row)}
                  title={t('loads.crud.editLoad')}
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )
            );
          })()}
          {!superReadOnly &&
            (isAdmin() ||
            isManagerOrAdmin() ||
            (isOperator() && row.status === 'REQUESTED') ||
            (isPeseur() && row.status !== 'DELIVERED')) &&
            row.canDeleteNoCertificateOrBill === true && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDeleteLoad(row)}
              title={t('loads.crud.deleteLoad')}
              className="text-red-600 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
          {hasPeseurDeliveredPaidPonctuelAction && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleStatusChange(row, 'DELIVERED')}
              title={t('loads.actions.markDelivered')}
              className="text-green-600 hover:text-green-700 bg-white/80 hover:bg-white border border-slate-200/80"
            >
              <CheckCircleIcon className="h-4 w-4" />
            </Button>
          )}
          </div>
        </div>
        );
      },
    },
  ];

  // Filter columns based on user role
  const columns = allColumns;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ArchiveBoxIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('loads.title')}</h1>
            <p className="text-gray-600">{t('loads.allLoads')}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {!superReadOnly &&
            (isPeseur() || (isOperator() && preTareRequestsAllowed && clientEligibleBilling)) && (
            <Button onClick={handleAddLoad}>
              <PlusIcon className="h-4 w-4 mr-2" />
              {isPeseur() ? t('loads.crud.addLoad') : t('loads.crud.requestLoad')}
            </Button>
          )}
        </div>
      </div>

      {isManager() && String(filters.peseurId || '').trim() && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-950">
            {t('loads.peseurFilterBanner', { name: peseurFilterDisplayName })}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={clearPeseurFilter}>
            {t('loads.clearPeseurFilter')}
          </Button>
        </div>
      )}

      {/* Filters — search always visible; dates, client, type, status in collapsible panel */}
      <div className="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="relative w-full sm:max-w-2xl flex-1 min-w-0">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
            <Input
              placeholder={t('loads.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 self-stretch sm:self-auto inline-flex items-center justify-center gap-2"
            onClick={() => setMoreFiltersOpen((o) => !o)}
            aria-expanded={moreFiltersOpen}
          >
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-600 transition-transform ${moreFiltersOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
            {moreFiltersOpen ? t('loads.hideFilters') : t('loads.moreFilters')}
          </Button>
        </div>

        {moreFiltersOpen && (
          <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="w-full">
                <Select
                  placeholder={t('loads.selectClient')}
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  options={clients?.map(client => ({
                    value: client.id,
                    label: client.companyName
                  })) || []}
                />
              </div>

              {/* Hide transaction type filter for Admin, Manager, and Driver users */}
              {!isAdmin() && !isManagerOrAdmin() && !isDriver() && (
                <div className="w-full">
                  <Select
                    placeholder={t('loads.selectTransactionType')}
                    value={filters.transactionType}
                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                    options={[
                      { value: 'IMPORT', label: t('weighing.import') },
                      { value: 'EXPORT', label: t('weighing.export') },
                    ]}
                  />
                </div>
              )}

              <div className="w-full">
                <Input
                  type="date"
                  placeholder={t('loads.startDate')}
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div className="w-full">
                <Input
                  type="date"
                  placeholder={t('loads.endDate')}
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <span className="block text-sm font-medium text-gray-700 mb-2">{t('loads.status')}</span>
              <div className="flex flex-wrap gap-x-4 gap-y-2 p-3 border border-gray-200 rounded-md bg-gray-50/80">
                {LOAD_FILTER_STATUS_ORDER.map((code) => (
                  <label
                    key={code}
                    className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      className="checkbox-app"
                      checked={sortStatusFilterSelection(filters.status).includes(code)}
                      onChange={() => toggleStatusFilter(code)}
                    />
                    <span>{t(`loads.statuses.${code.toLowerCase()}`)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1.5 min-h-[1.25rem]">
                {sortStatusFilterSelection(filters.status).length === 0
                  ? t('loads.statusFilterAll')
                  : t('loads.statusFilterSelected', {
                      list: sortStatusFilterSelection(filters.status)
                        .map((code) => t(`loads.statuses.${code.toLowerCase()}`))
                        .join(', '),
                    })}
              </p>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={resetFilters}>
                {t('common.clear')}
              </Button>
            </div>
          </>
        )}

        {isPeseur() && (
          <div className="mt-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
              <input
                type="checkbox"
                className="checkbox-app"
                checked={Boolean(filters.secondPassPending)}
                onChange={(e) => handleFilterChange('secondPassPending', e.target.checked)}
              />
              <span>{t('loads.filterSecondPassPending')}</span>
            </label>
          </div>
        )}
      </div>

      {/* Loads Table */}
      <Table
        columns={columns}
        data={loads}
        loading={loading}
        pagination={pagination}
        onPageChange={goToPage}
        emptyMessage={t('loads.noLoadsFound')}
      />

      <LoadDetailViewModal
        isOpen={showLoadModal}
        onClose={() => {
          setShowLoadModal(false);
          setLoadDetailReturn(null);
        }}
        load={selectedLoad}
        returnNavigation={loadDetailReturn}
      />

      <Modal
        isOpen={rowEmailModalOpen}
        onClose={() => {
          setRowEmailModalOpen(false);
          setRowEmailLoad(null);
          setRowEmailAddress('');
        }}
        title={t('loads.modal.sendTicketTitle')}
        footer={
          <>
            <Button variant="outline" onClick={() => setRowEmailModalOpen(false)}>
              {t('loads.modal.cancel')}
            </Button>
            <Button onClick={sendRowTicketEmail} loading={actionLoading}>
              {t('loads.modal.sendEmail')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('loads.modal.sendTicketDescription', {
              loadId: rowEmailLoad?.ticket?.ticketNumber || rowEmailLoad?.id,
            })}
          </p>
          <Input
            label={t('loads.modal.emailAddressLabel')}
            type="email"
            value={rowEmailAddress}
            onChange={(e) => setRowEmailAddress(e.target.value)}
            placeholder={t('loads.modal.emailAddressPlaceholder')}
            required
          />
        </div>
      </Modal>

      {/* Add Load Modal */}
      <AddTicketModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddTicketContinueLoadId(null);
          setAddTicketOpenFromAcceptedAwaitingTare(false);
        }}
        onSuccess={handleAddSuccess}
        continueLoadId={addTicketContinueLoadId}
        continueFromLoadTareFirst={addTicketOpenFromAcceptedAwaitingTare}
      />

      {/* Edit Load Modal */}
      <EditLoadModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedLoad(null);
        }}
        onSuccess={handleEditSuccess}
        load={selectedLoad}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedLoad(null);
        }}
        onConfirm={confirmDelete}
        loading={actionLoading}
        title={t('loads.crud.deleteLoadTitle')}
        message={
          isPeseur() ? (
            <div className="space-y-3">
              <p>
                {t('loads.crud.deleteConfirm', {
                  truck: String(selectedLoad?.truck?.plateNumber || '').trim() || '—',
                  client: String(getLoadClientDisplayName(selectedLoad) || '').trim() || '—',
                })}
              </p>
              <p className="border-t border-amber-200 pt-3 text-amber-950 font-medium leading-snug">
                {t('loads.crud.deletePonctuelBillWarning')}
              </p>
            </div>
          ) : (
            t('loads.crud.deleteConfirm', {
              truck: String(selectedLoad?.truck?.plateNumber || '').trim() || '—',
              client: String(getLoadClientDisplayName(selectedLoad) || '').trim() || '—',
            })
          )
        }
        confirmText={t('common.delete')}
        variant="danger"
      />

      <LoadSupplementaryTaxModal
        isOpen={showTsModal}
        onClose={handleCloseTsModal}
        onConfirm={handleConfirmTs}
        loading={actionLoading}
      />
    </div>
  );
};

export default LoadsPage;